import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCamera, FaTrash, FaStar } from "react-icons/fa";
import Navbar from "./Navbar";

const PhotoUploader = ({ fetchWithRetry, logout }) => {
  const [photos, setPhotos] = useState([]);
  const [photoHistory, setPhotoHistory] = useState([]);
  const [primaryPhoto, setPrimaryPhoto] = useState({ photoUrl: "", caption: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [caption, setCaption] = useState("");
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPhotosAndHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const profileRes = await fetchWithRetry({
          method: "get",
          url: "/api/users/profile",
        });
        const profileData = profileRes.data || {};
        if (profileData && profileData._id) {
          setPhotos(profileData.photos || []);
          setPrimaryPhoto(profileData.primaryPhoto || { photoUrl: "", caption: "" });
        } else {
          setError("Không thể tải danh sách ảnh từ server.");
        }

        const historyRes = await fetchWithRetry({
          method: "get",
          url: "/api/users/photo-history",
        });
        setPhotoHistory(historyRes.data.photoHistory || []);
      } catch (err) {
        console.error("Lỗi khi tải dữ liệu:", err);
        if (err.response?.status === 404) {
          setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
          setTimeout(() => {
            logout();
            navigate("/login");
          }, 2000);
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
          setTimeout(() => {
            logout();
            navigate("/login");
          }, 2000);
        } else {
          setError(err.response?.data?.message || err.message || "Không thể tải dữ liệu từ server.");
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchNotifications = async () => {
      try {
        const response = await fetchWithRetry({
          method: "get",
          url: "/api/users/notifications",
        });
        setNotifications(response.data);
        setUnreadNotificationCount(response.data.filter((n) => !n.read).length);
      } catch (error) {
        console.error("Lỗi khi tải thông báo:", error);
      }
    };

    fetchPhotosAndHistory();
    fetchNotifications();
  }, [fetchWithRetry, logout, navigate]);

  const checkTokenValidity = async () => {
    try {
      await fetchWithRetry({
        method: "get",
        url: "/api/users/profile",
      });
      return true;
    } catch (error) {
      console.error("Lỗi kiểm tra token:", error);
      return false;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (photos.length >= 5) {
      setError("Bạn chỉ có thể tải lên tối đa 5 ảnh!");
      return;
    }

    setSelectedFile(file);
    setShowCaptionModal(true);
  };

  const handlePhotoUpload = async () => {
    if (!selectedFile) return;

    const isTokenValid = await checkTokenValidity();
    if (!isTokenValid) {
      setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
      return;
    }

    const formData = new FormData();
    formData.append("photo", selectedFile);
    formData.append("caption", caption || "");

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/upload-photo",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newPhotoUrl = response.data.photoUrl;
      const newCaption = response.data.caption;
      if (!newPhotoUrl) {
        throw new Error("Không nhận được URL ảnh từ server!");
      }

      setPhotos((prevPhotos) => [...prevPhotos, { photoUrl: newPhotoUrl, caption: newCaption }]);
      setPhotoHistory((prevHistory) => [
        ...prevHistory,
        { photoUrl: newPhotoUrl, caption: newCaption, uploadedAt: new Date(), status: "active" },
      ]);

      if (photos.length === 0) {
        setPrimaryPhoto({ photoUrl: newPhotoUrl, caption: newCaption });
      }

      setSuccess("Tải ảnh lên thành công!");
      setShowCaptionModal(false);
      setCaption("");
      setSelectedFile(null);
    } catch (err) {
      console.error("Lỗi khi upload ảnh:", err);
      if (err.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(err.response?.data?.message || err.message || "Không thể upload ảnh!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoDelete = async (photoUrl) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await fetchWithRetry({
        method: "delete",
        url: "/api/users/delete-photo",
        data: { photoUrl },
      });

      setPhotos((prevPhotos) => prevPhotos.filter((photo) => photo.photoUrl !== photoUrl));
      setPhotoHistory((prevHistory) =>
        prevHistory.map((entry) =>
          entry.photoUrl === photoUrl ? { ...entry, status: "deleted" } : entry
        )
      );

      if (primaryPhoto.photoUrl === photoUrl) {
        if (photos.length > 1) {
          setPrimaryPhoto(photos[0]);
        } else {
          setPrimaryPhoto({ photoUrl: "", caption: "" });
        }
      }

      setSuccess("Xóa ảnh thành công!");
    } catch (err) {
      console.error("Lỗi khi xóa ảnh:", err);
      if (err.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(err.response?.data?.message || err.message || "Không thể xóa ảnh!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimaryPhoto = async (photoUrl) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/set-primary-photo",
        data: { photoUrl },
      });

      const newPrimaryPhoto = response.data.primaryPhoto;
      setPrimaryPhoto(newPrimaryPhoto);
      setSuccess("Đặt ảnh chính thành công!");
    } catch (err) {
      console.error("Lỗi khi đặt ảnh chính:", err);
      if (err.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(err.response?.data?.message || err.message || "Không thể đặt ảnh chính!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = (notificationId, action) => {
    if (action === "read") {
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadNotificationCount(notifications.filter((n) => !n.read).length);
    } else if (action === "delete") {
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      setUnreadNotificationCount(notifications.filter((n) => !n.read).length);
    }
  };

  return (
    <div className="font-sans min-h-screen bg-gray-100">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-4xl mx-auto p-6">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Quản lý ảnh</h2>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        {success && <p className="text-green-500 text-center mb-4">{success}</p>}

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4">Ảnh chính</h2>
            {primaryPhoto.photoUrl ? (
              <div className="relative w-40 h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                <img
                  src={primaryPhoto.photoUrl}
                  alt="Ảnh chính"
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => (e.target.src = "https://placehold.co/150")}
                />
                <div className="absolute bottom-2 left-2 bg-gray-800 text-white text-sm px-2 py-1 rounded">
                  {primaryPhoto.caption || "Không có mô tả"}
                </div>
                <div className="absolute top-2 right-2 bg-yellow-500 text-white p-1 rounded-full">
                  <FaStar />
                </div>
              </div>
            ) : (
              <p className="text-gray-600">Chưa có ảnh chính.</p>
            )}
          </div>

          <h2 className="text-2xl font-bold mb-4">Tải ảnh</h2>
          <p className="text-gray-600 mb-4">
            Cách nhiều quản nhất để người khác chú ý đến bạn là thêm hình vào hồ sơ
          </p>

          <div className="grid grid-cols-5 gap-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="relative w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center"
              >
                {photos[index] ? (
                  <>
                    <img
                      src={photos[index].photoUrl}
                      alt={`Ảnh hồ sơ thứ ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => (e.target.src = "https://placehold.co/150")}
                    />
                    <div className="absolute bottom-2 left-2 bg-gray-800 text-white text-sm px-2 py-1 rounded">
                      {photos[index].caption || "Không có mô tả"}
                    </div>
                    <button
                      onClick={() => handlePhotoDelete(photos[index].photoUrl)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                    <button
                      onClick={() => handleSetPrimaryPhoto(photos[index].photoUrl)}
                      className="absolute top-2 left-2 bg-yellow-500 text-white p-2 rounded-full hover:bg-yellow-600"
                      disabled={loading || primaryPhoto.photoUrl === photos[index].photoUrl}
                    >
                      <FaStar />
                    </button>
                  </>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={loading}
                    />
                    <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <FaCamera className="text-gray-500 text-2xl" />
                    </div>
                    <span className="absolute top-2 left-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                      {index + 1}
                    </span>
                  </label>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6">
            <details className="group">
              <summary className="cursor-pointer text-gray-600 flex items-center">
                <span className="mr-2">▼</span>
                Không thể tải ảnh? Hãy thử các lựa chọn thay thế này ➔
              </summary>
              <div className="mt-2 pl-6 text-gray-600">
                <p>
                  <strong>Hướng Dẫn Ảnh</strong>
                </p>
                <p>Cách chọn ảnh chính phù hợp:</p>
                <ul className="list-disc pl-5">
                  <li>Bạn đang mỉm cười! :)</li>
                  <li>Ảnh chỉ có bạn</li>
                  <li>Ảnh rõ nét, không bị mờ</li>
                  <li>Thay rõ mặt của bạn</li>
                  <li>Chất lượng tốt và đủ ánh sáng</li>
                  <li>Không chứa ảnh khỏa thân</li>
                </ul>
              </div>
            </details>
          </div>

          <div className="mt-6">
            <h2 className="text-2xl font-bold mb-4">Lịch sử upload ảnh</h2>
            {photoHistory.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {photoHistory.map((entry, index) => (
                  <div key={index} className="relative w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                    <img
                      src={entry.photoUrl}
                      alt={`Ảnh lịch sử ${index + 1}`}
                      className={`w-full h-full object-cover rounded-lg ${entry.status === "deleted" ? "opacity-50" : ""}`}
                      onError={(e) => (e.target.src = "https://placehold.co/150")}
                    />
                    <div className="absolute bottom-2 left-2 bg-gray-800 text-white text-sm px-2 py-1 rounded">
                      {new Date(entry.uploadedAt).toLocaleString()}
                    </div>
                    <div className="absolute bottom-2 right-2 bg-gray-800 text-white text-sm px-2 py-1 rounded">
                      {entry.caption || "Không có mô tả"}
                    </div>
                    <div className="absolute top-2 right-2 bg-gray-800 text-white text-sm px-2 py-1 rounded">
                      {entry.status === "active" ? "Đang sử dụng" : "Đã xóa"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">Chưa có lịch sử upload ảnh.</p>
            )}
          </div>
        </div>
      </div>

      {showCaptionModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="modal-content"
            style={{
              width: "400px",
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            <h3 className="text-lg font-bold mb-4">Thêm mô tả cho ảnh</h3>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Nhập mô tả cho ảnh (tùy chọn)"
              className="w-full border p-2 rounded shadow-sm mb-4"
              rows="3"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCaptionModal(false);
                  setCaption("");
                  setSelectedFile(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg shadow hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handlePhotoUpload}
                className="px-4 py-2 bg-green-500 text-white rounded-lg shadow hover:bg-green-600"
                disabled={loading}
              >
                {loading ? "Đang tải..." : "Tải lên"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;